import {
	commands,
	getBreaksNeededForEmptyLineBefore,
	getBreaksNeededForEmptyLineAfter,
	type ICommand,
} from "@uiw/react-md-editor/nohighlight";
import {
	Bold,
	Code,
	Heading,
	Italic,
	Link,
	List,
	ListOrdered,
	ListTodo,
	TextQuote,
	type LucideIcon,
} from "lucide-react";

export interface ToolbarAction {
	label: string;
	icon: LucideIcon;
	command: ICommand;
}

export const FORMAT_ACTIONS: ToolbarAction[] = [
	{ label: "Add heading text", icon: Heading, command: commands.heading3 },
	{ label: "Add bold text", icon: Bold, command: commands.bold },
	{ label: "Add italic text", icon: Italic, command: commands.italic },
	{ label: "Add a quote", icon: TextQuote, command: commands.quote },
	{ label: "Add code", icon: Code, command: commands.code },
	{ label: "Add a link", icon: Link, command: commands.link },
];

export const LIST_ACTIONS: ToolbarAction[] = [
	{
		label: "Add a numbered list",
		icon: ListOrdered,
		command: commands.orderedListCommand,
	},
	{
		label: "Add a bulleted list",
		icon: List,
		command: commands.unorderedListCommand,
	},
	{
		label: "Add a task list",
		icon: ListTodo,
		command: commands.checkedListCommand,
	},
];

// Registered on MDEditor so the library's keyboard shortcuts keep working
// with its own toolbar hidden.
export const SHORTCUT_COMMANDS = [...FORMAT_ACTIONS, ...LIST_ACTIONS].map(
	(action) => action.command,
);

export const SLASH_SNIPPETS = [
	{ label: "Code block", text: "```\n\n```" },
	{
		label: "Table",
		text: "| Header | Header |\n| ------ | ------ |\n| Cell   | Cell   |",
	},
];

export function insertCommand(text: string, { block = false } = {}): ICommand {
	return {
		name: "insert",
		keyCommand: "insert",
		execute: (state, api) => {
			if (!block) {
				api.replaceSelection(text);
				return;
			}
			// Block snippets (tables, fences) need blank lines around them to parse.
			const { text: doc, selection } = state;
			const before = getBreaksNeededForEmptyLineBefore(doc, selection.start);
			const after = getBreaksNeededForEmptyLineAfter(doc, selection.end);
			api.replaceSelection("\n".repeat(before) + text + "\n".repeat(after));
		},
	};
}

const IS_MAC =
	typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const KEY_LABELS: Record<string, string> = {
	ctrlcmd: IS_MAC ? "Cmd" : "Ctrl",
	ctrl: "Ctrl",
	shift: "Shift",
};

// "ctrlcmd+b" -> "Add bold text <Cmd+B>"
export function actionTooltip({ label, command }: ToolbarAction): string {
	if (!command.shortcuts) return label;
	const keys = command.shortcuts
		.split("+")
		.map((key) => KEY_LABELS[key] ?? key.toUpperCase());
	return `${label} <${keys.join("+")}>`;
}
